import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { filterFieldsFromColumns } from '../components/common/columns/columnModel';
import { readFilters, toApiParams, urlKeysOf, writeFilter } from '../components/common/filters/registry';

const SEARCH_DEBOUNCE_MS = 300;
const MAX_LIMIT = 100;

const buildInitialQuery = (initialFilters, initialSearch, fields) => {
    const sp = new URLSearchParams();
    if (initialSearch) sp.set('search', initialSearch);
    fields.forEach((f) => {
        if (initialFilters[f.key] !== undefined) writeFilter(f, sp, initialFilters[f.key]);
    });
    return sp.toString();
};

/**
 * State + fetching for a server-paged DataTable.
 *
 * `fetcher(params, filterValues)` must resolve to `{ results, pagination: { page, limit, total, totalPages } }`.
 * params = { page, limit, sort, dir, search, filter_*: value }, matching lib/paginate.js.
 *
 * Table state (search, sort, page, page size and every filter in `fields`) lives in the URL
 * query string, so a filtered view is linkable and Back steps through filter changes.
 * Tables inside modals pass `urlState: false` and keep the same state in memory instead.
 *
 * columns: the table's column declarations. Filter fields are derived from each column's
 *   `filter` (one source of truth: a filterable field is always a column of the table).
 * presetFields: filters that belong to no column (the data is not in the row, e.g. a lockout
 *   state). They stay in the URL and show as chips, so drill-down links keep working, but the
 *   Filters panel does not offer them.
 * fields: filter field definitions (components/common/filters/registry.js), for tables that
 *   have no column declarations (e.g. client-side lists). Ignored when `columns` is given.
 *   Values are read from / written to the URL under each field's key (e.g. ?status=1,0).
 * deps: re-runs the query (from page 1 once the table has loaded) when they change.
 * scopeKey + scopedFilters: when scopeKey (the selected app) changes from one value to
 *   another, those filters are dropped (a tenant or role belongs to one app). A deep link's
 *   first resolution of the app never clears them.
 * enabled: false skips fetching.
 * Spread `tableProps` into <DataTable />.
 */
export default function useServerTable(
    fetcher,
    {
        deps = [],
        enabled = true,
        defaultSort = null,
        defaultLimit = 20,
        columns,
        presetFields = [],
        fields: declaredFields = [],
        urlState = true,
        scopeKey,
        scopedFilters = [],
        initialFilters = {},
        initialSearch = '',
    } = {}
) {
    const fields = columns
        ? [...filterFieldsFromColumns(columns), ...presetFields.map((f) => ({ ...f, preset: true }))]
        : declaredFields;
    const fieldsRef = useRef(fields);
    fieldsRef.current = fields;
    const fieldsSig = fields.map((f) => `${f.key}:${f.type}`).join('|');

    // --- state store: the URL, or an in-memory query string with the same shape ----------
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [localQuery, setLocalQuery] = useState(() =>
        urlState ? '' : buildInitialQuery(initialFilters, initialSearch, fields)
    );
    const queryString = urlState ? searchParams.toString() : localQuery;
    const pathnameRef = useRef(location.pathname);
    pathnameRef.current = location.pathname;

    /** Apply `mutate(URLSearchParams)` to the state. Push by default so Back undoes it. */
    const write = useCallback(
        (mutate, { replace = false } = {}) => {
            if (!urlState) {
                setLocalQuery((prev) => {
                    const sp = new URLSearchParams(prev);
                    mutate(sp);
                    return sp.toString();
                });
                return;
            }
            // Read the live URL (not the render's copy) so two writes in one tick both land.
            const current = window.location.search.replace(/^\?/, '');
            const sp = new URLSearchParams(current);
            mutate(sp);
            const next = sp.toString();
            if (next === current) return;
            navigate({ pathname: pathnameRef.current, search: next ? `?${next}` : '' }, { replace });
        },
        [urlState, navigate]
    );

    const state = useMemo(() => {
        const sp = new URLSearchParams(queryString);
        const page = Math.max(parseInt(sp.get('page'), 10) || 1, 1);
        const rawLimit = parseInt(sp.get('limit'), 10);
        const limit = rawLimit > 0 ? Math.min(rawLimit, MAX_LIMIT) : defaultLimit;
        const sortKey = sp.get('sort');
        const sort = sortKey ? { key: sortKey, dir: sp.get('dir') === 'desc' ? 'desc' : 'asc' } : defaultSort;
        const search = (sp.get('search') || '').trim();
        const filters = readFilters(fieldsRef.current, sp);
        return { page, limit, sort, search, filters };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryString, fieldsSig, defaultLimit, JSON.stringify(defaultSort)]);

    // --- search box: immediate input, debounced into the state ---------------------------
    const [searchInput, setSearchInput] = useState(state.search);
    const searchInputRef = useRef(searchInput);
    searchInputRef.current = searchInput;
    useEffect(() => {
        // Back / a link / Clear all changed the committed search: mirror it in the box.
        if (state.search !== searchInputRef.current.trim()) setSearchInput(state.search);
    }, [state.search]);
    useEffect(() => {
        const t = setTimeout(() => {
            const next = searchInput.trim();
            write(
                (sp) => {
                    if ((sp.get('search') || '').trim() === next) return;
                    if (next) sp.set('search', next);
                    else sp.delete('search');
                    sp.delete('page');
                },
                { replace: true }
            );
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [searchInput, write]);

    // --- scope / deps changes ------------------------------------------------------------
    const prevScope = useRef(scopeKey);
    useEffect(() => {
        const before = prevScope.current;
        prevScope.current = scopeKey;
        if (!before || before === scopeKey || scopedFilters.length === 0) return;
        const scoped = fieldsRef.current.filter((f) => scopedFilters.includes(f.key));
        write(
            (sp) => {
                scoped.forEach((f) => writeFilter(f, sp, undefined));
                sp.delete('page');
            },
            { replace: true }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopeKey]);

    const depsKey = JSON.stringify(deps);
    const lastEnabledDeps = useRef(null);

    // --- fetching -----------------------------------------------------------------------
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: defaultLimit,
        total: 0,
        totalPages: 0,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [reloadToken, setReloadToken] = useState(0);
    const requestId = useRef(0);
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    const apiParams = useMemo(() => {
        const params = { page: state.page, limit: state.limit };
        if (state.sort?.key) {
            params.sort = state.sort.key;
            params.dir = state.sort.dir;
        }
        if (state.search) params.search = state.search;
        Object.assign(params, toApiParams(fieldsRef.current, state.filters));
        return params;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);
    const paramsKey = JSON.stringify(apiParams);

    useEffect(() => {
        if (!enabled) {
            setRows([]);
            setPagination((p) => ({ ...p, total: 0, totalPages: 0 }));
            return;
        }
        // Switching what is listed (another app, another tab) starts again from page 1;
        // the very first load keeps a deep link's ?page=.
        if (lastEnabledDeps.current !== null && lastEnabledDeps.current !== depsKey) {
            lastEnabledDeps.current = depsKey;
            if (state.page !== 1) {
                write((sp) => sp.delete('page'), { replace: true });
                return;
            }
        }
        lastEnabledDeps.current = depsKey;

        const id = ++requestId.current;
        setLoading(true);
        setError('');
        Promise.resolve(fetcherRef.current(apiParams, state.filters))
            .then((data) => {
                if (id !== requestId.current) return;
                const next = data?.pagination || { page: state.page, limit: state.limit, total: 0, totalPages: 0 };
                // A delete (or a stale link) can point past the last page: step back.
                if (next.totalPages > 0 && state.page > next.totalPages) {
                    write((sp) => sp.set('page', String(next.totalPages)), { replace: true });
                    return;
                }
                setRows(data?.results || []);
                setPagination(next);
            })
            .catch((err) => {
                if (id !== requestId.current) return;
                setRows([]);
                setError(err?.response?.data?.message || err?.message || 'Failed to load data');
            })
            .finally(() => {
                if (id === requestId.current) setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, paramsKey, depsKey, reloadToken]);

    // --- actions ------------------------------------------------------------------------
    const reload = useCallback(() => setReloadToken((n) => n + 1), []);

    const onSort = useCallback(
        (key) =>
            write((sp) => {
                const cur = sp.get('sort') ? { key: sp.get('sort'), dir: sp.get('dir') || 'asc' } : defaultSort;
                let next;
                if (cur?.key !== key) next = { key, dir: 'asc' };
                else if (cur.dir !== 'desc') next = { key, dir: 'desc' };
                else next = null; // third click returns to the default order
                if (next) {
                    sp.set('sort', next.key);
                    sp.set('dir', next.dir);
                } else {
                    sp.delete('sort');
                    sp.delete('dir');
                }
                sp.delete('page');
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [write, JSON.stringify(defaultSort)]
    );

    const onPageChange = useCallback(
        (page) =>
            write((sp) => {
                if (page > 1) sp.set('page', String(page));
                else sp.delete('page');
            }),
        [write]
    );

    const onLimitChange = useCallback(
        (limit) =>
            write((sp) => {
                if (limit === defaultLimit) sp.delete('limit');
                else sp.set('limit', String(limit));
                sp.delete('page');
            }),
        [write, defaultLimit]
    );

    /** Set one filter (undefined / '' / [] clears it). */
    const setFilter = useCallback(
        (key, value) => {
            const field = fieldsRef.current.find((f) => f.key === key);
            if (!field) return;
            write((sp) => {
                writeFilter(field, sp, value);
                sp.delete('page');
            });
        },
        [write]
    );

    const clearFilter = useCallback((key) => setFilter(key, undefined), [setFilter]);

    /** Clear every filter and the search. */
    const resetFilters = useCallback(() => {
        setSearchInput('');
        write((sp) => {
            urlKeysOf(fieldsRef.current).forEach((k) => sp.delete(k));
            sp.delete('search');
            sp.delete('page');
        });
    }, [write]);

    /** Clear every filter, keeping the search (panel / chip strip "Clear all"). */
    const clearAllFilters = useCallback(
        () =>
            write((sp) => {
                urlKeysOf(fieldsRef.current).forEach((k) => sp.delete(k));
                sp.delete('page');
            }),
        [write]
    );

    const activeFilterCount = fields.filter((f) => state.filters[f.key] !== undefined).length;

    return {
        rows,
        pagination,
        loading,
        error,
        reload,
        filters: state.filters,
        setFilter,
        clearFilter,
        resetFilters,
        clearAllFilters,
        tableProps: {
            rows,
            loading,
            error,
            sort: state.sort,
            onSort,
            search: searchInput,
            onSearchChange: setSearchInput,
            pagination,
            onPageChange,
            onLimitChange,
            hasActiveFilters: Boolean(searchInput.trim()) || activeFilterCount > 0,
            onResetFilters: resetFilters,
            filterFields: fields,
            filterValues: state.filters,
            onFilterChange: setFilter,
            onClearFilters: clearAllFilters,
        },
    };
}
